import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ArrowRight,
  Phone,
  MapPin,
  TrendingUp,
  ShieldCheck,
  Sparkles,
  BadgeDollarSign,
  LineChart,
  Rocket,
  Check,
  Home as HomeIcon,
  HardHat,
  Store,
  Hexagon,
  Triangle,
  Square,
  Diamond,
  Circle,
  Pentagon,
  Star,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import Nav from "@/components/site/Nav";
import Footer from "@/components/site/Footer";
import MobileCtaBar from "@/components/site/MobileCtaBar";
import { BOOKING_URL, SectionHead, fadeUp, stagger } from "@/components/site/shared";

/* ---------- HERO INBOUND CALLS CARD ---------- */
const CallRow = ({ day, num, service, time, delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, x: -20 }}
    animate={{ opacity: 1, x: 0 }}
    transition={{ delay, duration: 0.5 }}
    className="flex items-center gap-3 px-4 py-3 border-b border-white/5 last:border-0"
  >
    <div className="font-mono text-[10px] tracking-widest text-white/50 bg-white/5 rounded-md px-2 py-1 min-w-[52px] text-center">
      {day} {num}
    </div>
    <div className="flex-1 min-w-0">
      <div className="text-[13px] text-white font-medium truncate">New customer</div>
      <div className="text-[11px] text-white/50 truncate">{service}</div>
    </div>
    <div className="flex items-center gap-2 shrink-0">
      <span className="relative inline-flex w-2 h-2">
        <span className="absolute inset-0 rounded-full bg-emerald-400 arco-pulse-ring text-emerald-400/70" />
        <span className="relative rounded-full bg-emerald-400 w-2 h-2" />
      </span>
      <div className="font-mono text-[11px] text-white/60">{time}</div>
    </div>
  </motion.div>
);

const HeroCard = () => (
  <div className="relative arco-float w-full max-w-[440px]">
    {/* Glow behind card */}
    <div className="absolute -inset-8 bg-[#FF5C00]/20 blur-3xl rounded-[3rem] -z-10" />

    <div
      className="relative rounded-2xl border border-white/10 bg-[#141F3D]/90 backdrop-blur-2xl overflow-hidden shadow-[0_50px_120px_-30px_rgba(255,92,0,0.35)]"
      data-testid="hero-inbound-card"
    >
      <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-[#FF5C00]/15 flex items-center justify-center">
            <Phone className="w-4 h-4 text-[#FF5C00]" />
          </div>
          <div>
            <div className="text-[10px] text-white/50 uppercase tracking-widest font-mono">
              This week
            </div>
            <div className="text-white font-semibold text-sm">Inbound Calls</div>
          </div>
        </div>
        <div className="flex items-center gap-2 bg-[#FF5C00]/15 text-[#FF5C00] rounded-full px-3 py-1 text-xs font-bold">
          <span className="w-1.5 h-1.5 rounded-full bg-[#FF5C00]" />
          14 new
        </div>
      </div>

      <div className="py-1">
        <CallRow day="MON" num="10" service="Kitchen remodel" time="9:12 AM" delay={0.4} />
        <CallRow day="TUE" num="11" service="AC repair" time="11:30 AM" delay={0.55} />
        <CallRow day="WED" num="12" service="Roof inspection" time="2:04 PM" delay={0.7} />
        <CallRow day="THU" num="13" service="Panel upgrade" time="4:30 PM" delay={0.85} />
        <CallRow day="FRI" num="14" service="Landscape quote" time="8:47 AM" delay={1.0} />
      </div>

      <div className="flex items-center justify-between px-5 py-4 border-t border-white/10 bg-black/30">
        <div className="flex items-center gap-2 text-[11px]">
          <MapPin className="w-3.5 h-3.5 text-[#FF5C00]" />
          <span className="text-white/70">
            Ranked <span className="text-white font-semibold">#1</span> on Google Maps
          </span>
        </div>
        <button className="text-[11px] text-[#FF5C00] font-semibold hover:underline">
          + more this week
        </button>
      </div>
    </div>

    {/* Floating tag */}
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 1.2, duration: 0.6 }}
      className="absolute -left-6 top-16 hidden sm:flex items-center gap-2 bg-[#0C1732]/80 backdrop-blur border border-white/10 rounded-full px-3 py-1.5 text-[11px] text-white shadow-xl"
    >
      <TrendingUp className="w-3.5 h-3.5 text-emerald-400" />
      +212% call volume
    </motion.div>
  </div>
);

/* ---------- HERO ---------- */
const Hero = () => {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 500], [0, 100]);

  return (
    <section id="top" className="relative pt-32 md:pt-40 pb-20 md:pb-28 overflow-hidden">
      <div className="absolute inset-0 arco-hero-glow pointer-events-none" />
      <div className="absolute inset-0 arco-dots opacity-70 pointer-events-none" />
      <div className="absolute inset-0 arco-grain pointer-events-none" />

      <motion.div
        style={{ y }}
        className="max-w-7xl mx-auto px-6 md:px-10 relative grid lg:grid-cols-[1.15fr_1fr] gap-14 lg:gap-10 items-center"
      >
        <motion.div variants={stagger} initial="hidden" animate="show">
          <motion.div variants={fadeUp} className="inline-flex items-center gap-2 rounded-full border border-[#FF5C00]/30 bg-[#FF5C00]/8 px-3.5 py-1.5 text-[11px] font-bold uppercase tracking-[0.2em] text-[#FF5C00]">
            <span className="w-1.5 h-1.5 rounded-full bg-[#FF5C00]" />
            Built for local service businesses
          </motion.div>

          <motion.h1
            variants={fadeUp}
            data-testid="hero-headline"
            className="font-display mt-6 text-white text-5xl sm:text-6xl md:text-7xl lg:text-[5.5rem] font-black leading-[0.95] tracking-tighter"
          >
            Stop chasing leads.
            <br />
            <span className="text-[#FF5C00] italic font-black" style={{ fontFamily: '"Cabinet Grotesk", serif' }}>
              Start owning
            </span>{" "}
            <span className="text-white">your calls.</span>
          </motion.h1>

          <motion.p
            variants={fadeUp}
            className="mt-7 text-white/70 text-lg md:text-xl leading-relaxed max-w-xl"
          >
            Arco ranks your Google Business Profile so customers in your area call
            you directly. We guarantee <span className="text-white font-semibold">8&ndash;15 inbound calls</span> in your first 30 days.
            No shared leads. No ad spend. No long contracts.
          </motion.p>

          <motion.div variants={fadeUp} className="mt-9 flex flex-wrap items-center gap-3">
            <a
              href={BOOKING_URL}
              target="_blank"
              rel="noreferrer"
              data-testid="book-call-btn-hero"
              className="inline-flex arco-btn-primary items-center gap-2 rounded-full px-6 py-3.5 text-sm font-semibold"
            >
              Book a Call <ArrowRight className="w-4 h-4" />
            </a>
            <a
              href="#process"
              data-testid="how-it-works-btn"
              className="inline-flex arco-btn-outline items-center gap-2 rounded-full px-6 py-3.5 text-sm font-semibold"
            >
              See How It Works
            </a>
          </motion.div>

          <motion.ul
            variants={fadeUp}
            className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-3 max-w-xl"
          >
            {[
              "Exclusive Inbound Calls",
              "Zero Ad Spend",
              "Yours Alone, Never Shared",
              "8+ Calls Guaranteed",
            ].map((t) => (
              <li key={t} className="flex items-start gap-2 text-white/75 text-[13px]">
                <Check className="w-4 h-4 text-[#FF5C00] shrink-0 mt-0.5" />
                {t}
              </li>
            ))}
          </motion.ul>
        </motion.div>

        <div className="flex justify-center lg:justify-end">
          <HeroCard />
        </div>
      </motion.div>
    </section>
  );
};

/* ---------- MARQUEE ---------- */
const brands = [
  { name: "Summit Remodeling", Icon: Triangle },
  { name: "Anchor HVAC", Icon: Hexagon },
  { name: "Bright Spark Electric", Icon: Star },
  { name: "TrueLine Plumbing", Icon: Square },
  { name: "Redline Roofing", Icon: Diamond },
  { name: "Clearwater Pools", Icon: Circle },
  { name: "Ironclad Garage Doors", Icon: Pentagon },
  { name: "Evergreen Landscaping", Icon: Triangle },
];

const Marquee = () => (
  <section className="relative py-14 border-y border-white/10 bg-[#0A1329]/60 overflow-hidden">
    <div className="text-center mb-8">
      <p className="text-[11px] font-bold uppercase tracking-[0.25em] text-white/40">
        Ranking local businesses across the country
      </p>
    </div>
    <div className="relative">
      <div className="pointer-events-none absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-[#0C1732] to-transparent z-10" />
      <div className="pointer-events-none absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-[#0C1732] to-transparent z-10" />
      <div className="flex arco-marquee-track gap-14">
        {[...brands, ...brands, ...brands].map((b, i) => (
          <div key={i} className="flex items-center gap-3 text-white/40 hover:text-white/80 transition-colors">
            <b.Icon className="w-5 h-5" strokeWidth={1.5} />
            <span className="font-display text-lg font-bold whitespace-nowrap tracking-tight">
              {b.name}
            </span>
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* ---------- PROCESS ---------- */
const processSteps = [
  {
    n: "01",
    title: "We rank your profile",
    body:
      "Our team optimizes and ranks your Google Business Profile for the services you want, across your service area. No ad budget, no work on your end.",
    Icon: MapPin,
  },
  {
    n: "02",
    title: "You climb the map",
    body:
      "You start showing up at the top of Google Maps when local customers search for what you do. Within 2 to 4 weeks you are the obvious pick.",
    Icon: TrendingUp,
  },
  {
    n: "03",
    title: "Customers call you",
    body:
      "Ready-to-buy customers in your area call you directly, calls that are exclusively yours. Every week you get a report of exactly what came in.",
    Icon: Phone,
  },
];

const Process = () => (
  <section id="process" className="relative py-28 md:py-36">
    <div className="max-w-7xl mx-auto px-6 md:px-10">
      <SectionHead
        eyebrow="The Process"
        title="From invisible to"
        accent="number one."
        sub="A simple, transparent flow that turns your Google profile into a phone that rings."
      />

      <div className="mt-20 grid md:grid-cols-3 gap-6 relative">
        {/* connecting line */}
        <div className="hidden md:block absolute top-16 left-[16.66%] right-[16.66%] h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />

        {processSteps.map((s, i) => (
          <motion.div
            key={s.n}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, delay: i * 0.12 }}
            className="arco-card rounded-2xl p-8 relative"
            data-testid={`process-card-${i + 1}`}
          >
            <div className="flex items-start justify-between mb-6">
              <span
                className="font-display text-6xl md:text-7xl font-black leading-none"
                style={{
                  background: "linear-gradient(180deg, rgba(255,92,0,1), rgba(255,92,0,0.25))",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                {s.n}
              </span>
              <div className="w-11 h-11 rounded-xl bg-[#FF5C00]/12 border border-[#FF5C00]/25 flex items-center justify-center">
                <s.Icon className="w-5 h-5 text-[#FF5C00]" />
              </div>
            </div>
            <h3 className="font-display text-white text-2xl font-bold tracking-tight mb-3">
              {s.title}
            </h3>
            <p className="text-white/60 text-[15px] leading-relaxed">{s.body}</p>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

/* ---------- WHY ARCO ---------- */
const whyItems = [
  {
    title: "Calls, not leads",
    body:
      "We do not sell shared leads dialed by five other businesses. You get exclusive inbound calls from customers who reached out to you first.",
    Icon: Phone,
  },
  {
    title: "You own it",
    body:
      "Ads rent attention and die the moment you stop paying. Your ranked profile is an asset under your name that keeps producing and grows over time.",
    Icon: ShieldCheck,
  },
  {
    title: "Zero ad spend",
    body:
      "There is no media budget on top of this. We rank you organically on Google, so you are not renting clicks.",
    Icon: BadgeDollarSign,
  },
  {
    title: "8 to 15 calls, guaranteed",
    body:
      "You get at least 8 highly qualified inbound calls in month one, or we keep working your profile free until you do.",
    Icon: Sparkles,
  },
  {
    title: "Weekly proof",
    body:
      "Every week you get a clean report: calls, map views, direction requests, and where you rank. You always know what you are paying for.",
    Icon: LineChart,
  },
  {
    title: "Live in 24 hours",
    body:
      "We start within 24 hours of onboarding. Rankings typically move within 2 to 4 weeks, with your main area first.",
    Icon: Rocket,
  },
];

const WhyArco = () => (
  <section id="why" className="relative py-28 md:py-36 bg-[#0A1329]">
    <div className="absolute inset-0 arco-dots opacity-40 pointer-events-none" />
    <div className="max-w-7xl mx-auto px-6 md:px-10 relative">
      <SectionHead
        eyebrow="Why Arco"
        title="Why local owners"
        accent="run with Arco."
        sub="Six reasons owners trade shared leads and ad retainers for a phone that rings on its own."
      />

      <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {whyItems.map((w, i) => (
          <motion.div
            key={w.title}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.55, delay: i * 0.06 }}
            className="arco-card rounded-2xl p-7 group"
            data-testid={`why-card-${i + 1}`}
          >
            <div className="w-12 h-12 rounded-xl bg-[#FF5C00]/10 border border-[#FF5C00]/25 flex items-center justify-center mb-5 group-hover:bg-[#FF5C00]/20 transition-colors">
              <w.Icon className="w-5 h-5 text-[#FF5C00]" />
            </div>
            <h3 className="font-display text-white text-xl font-bold mb-2 tracking-tight">
              {w.title}
            </h3>
            <p className="text-white/55 text-[14px] leading-relaxed">{w.body}</p>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

/* ---------- STATS BAND ---------- */
const Stats = () => (
  <section id="results" className="relative py-28 md:py-36">
    <div className="max-w-7xl mx-auto px-6 md:px-10">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-14 md:gap-8 items-start border-t border-b border-white/10 py-14 md:py-24">
        {[
          { big: "8–15", label: "Inbound calls in your first 30 days" },
          { big: "1:1", label: "Every call, exclusively yours" },
          { big: "$0", label: "You spend on ads" },
        ].map((s, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: i * 0.1 }}
            className="flex flex-col items-center text-center"
            data-testid={`stat-${i + 1}`}
          >
            <div
              className="font-display text-[#FF5C00] text-7xl md:text-8xl lg:text-[8rem] font-black leading-none tracking-tighter flex items-baseline justify-center h-[7rem] md:h-[9rem] lg:h-[10rem]"
              style={{ textShadow: "0 8px 60px rgba(255,92,0,0.35)" }}
            >
              {s.big}
            </div>
            <div className="mt-6 text-white/70 text-sm md:text-base max-w-[220px]">
              {s.label}
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

/* ---------- SEGMENTS ---------- */
const segments = [
  {
    title: "Home & Property Services",
    body:
      "Remodelers, HVAC, plumbing, electrical, roofing, landscaping, pools. The trades homeowners search for and call.",
    Icon: HomeIcon,
  },
  {
    title: "Contractors & Restoration",
    body:
      "General contractors and restoration crews who need a steady pipeline of local, high-intent calls.",
    Icon: HardHat,
  },
  {
    title: "Local & Professional Services",
    body:
      "Med spas, clinics, law offices, pest control, and any local business that lives or dies by the phone.",
    Icon: Store,
  },
];

const Segments = () => (
  <section className="relative py-28 md:py-36 bg-[#0A1329]">
    <div className="max-w-7xl mx-auto px-6 md:px-10">
      <SectionHead
        eyebrow="Who it is for"
        title="One system,"
        accent="every local trade."
        sub="Whether you install, repair, remodel, or serve your town — if customers find you on Google, Arco puts you at the top."
      />
      <div className="mt-16 grid md:grid-cols-3 gap-5">
        {segments.map((s, i) => (
          <motion.div
            key={s.title}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6, delay: i * 0.1 }}
            className="arco-card rounded-2xl p-8 relative overflow-hidden"
            data-testid={`segment-card-${i + 1}`}
          >
            <div className="absolute -top-8 -right-8 w-40 h-40 arco-dots opacity-40" />
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#FF5C00] to-[#FF7326] flex items-center justify-center mb-6 shadow-[0_10px_30px_-8px_rgba(255,92,0,0.6)]">
              <s.Icon className="w-6 h-6 text-black" strokeWidth={2.2} />
            </div>
            <h3 className="font-display text-white text-2xl font-bold mb-3 tracking-tight">
              {s.title}
            </h3>
            <p className="text-white/60 text-[15px] leading-relaxed">{s.body}</p>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

/* ---------- FAQ ---------- */
const faqs = [
  {
    q: "How is this different from buying shared leads off Angi or HomeAdvisor?",
    a: "Those platforms sell the same customer to four or five businesses at once, so you are racing to call first and competing on price. With Arco, every call is exclusively yours. The customer searched, found you at the top, and called you directly.",
  },
  {
    q: "Do I need an ad budget on top of this?",
    a: "No. There is no media spend. We rank your Google Business Profile organically, so you are not paying for clicks or leads on top of the fee.",
  },
  {
    q: "How fast will I start getting calls?",
    a: "We are live within 24 hours of onboarding. Google rankings typically move within 2 to 4 weeks, with your main city first, then expanding outward.",
  },
  {
    q: "What if I do not get enough calls?",
    a: "You get at least 8 highly qualified inbound calls in your first month, or we keep working your profile free until you do. The risk is on us.",
  },
  {
    q: "What counts as a qualified call?",
    a: "Someone interested in your service, inside your service area, who actually called your business about the work. A real customer, not a tire-kicker or a shared form.",
  },
  {
    q: "Which businesses and areas do you cover?",
    a: "Any local service business in the US. Each location ranks across roughly a 15 to 25 mile radius, and we take a limited number of businesses per market to keep it exclusive.",
  },
];

const FAQ = () => (
  <section id="faq" className="relative py-28 md:py-36">
    <div className="max-w-3xl mx-auto px-6 md:px-10">
      <SectionHead
        eyebrow="FAQ"
        title="Questions,"
        accent="answered."
        sub="Straight answers to what every owner asks before signing on."
      />
      <div className="mt-14">
        <Accordion type="single" collapsible defaultValue="item-0" className="space-y-3">
          {faqs.map((f, i) => (
            <AccordionItem
              key={i}
              value={`item-${i}`}
              className="border border-white/10 rounded-xl bg-[#141F3D] px-6 [&[data-state=open]]:border-[#FF5C00]/40 [&[data-state=open]]:bg-[#1B274A] transition-all"
            >
              <AccordionTrigger
                data-testid={`faq-trigger-${i}`}
                className="text-left text-white font-semibold text-[15px] md:text-base hover:no-underline py-5 [&[data-state=open]]:text-[#FF5C00] [&>svg]:text-white/60"
              >
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-white/65 text-[14px] md:text-[15px] leading-relaxed pb-5">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </div>
  </section>
);

/* ---------- FINAL CTA ---------- */
const FinalCTA = () => (
  <section className="relative py-32 md:py-44 overflow-hidden">
    <div className="absolute inset-0 arco-cta-glow pointer-events-none" />
    <div className="absolute inset-0 arco-dots opacity-40 pointer-events-none" />
    <div className="max-w-4xl mx-auto px-6 md:px-10 text-center relative">
      <motion.h2
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.7 }}
        className="font-display text-white text-5xl sm:text-6xl md:text-8xl lg:text-9xl font-black tracking-tighter leading-[0.9]"
      >
        Your phone,
        <br />
        <span
          className="text-[#FF5C00] italic"
          style={{ fontFamily: '"Cabinet Grotesk", serif' }}
        >
          ringing.
        </span>
      </motion.h2>
      <motion.p
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: 0.15 }}
        className="mt-8 text-white/70 text-base md:text-lg max-w-xl mx-auto"
      >
        Stop paying for leads three other businesses are also calling. Book a quick
        fit call and we will show you what exclusive inbound looks like.
      </motion.p>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: 0.25 }}
        className="mt-10 flex flex-col items-center gap-4"
      >
        <a
          href={BOOKING_URL}
          target="_blank"
          rel="noreferrer"
          data-testid="book-call-btn-cta"
          className="inline-flex arco-btn-primary items-center gap-2 rounded-full px-8 py-4 text-base font-semibold"
        >
          Book a Call <ArrowRight className="w-5 h-5" />
        </a>
        <p className="text-white/40 text-xs">
          We onboard a limited number of businesses per market each month.
        </p>
      </motion.div>
    </div>
  </section>
);

/* ---------- PAGE ---------- */
export default function Landing() {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace("#", "");
      // wait a tick for the page to render
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    }
  }, [location]);

  return (
    <main
      data-testid="landing-page"
      className="min-h-screen bg-[#0C1732] text-white overflow-x-hidden"
    >
      <Nav />
      <Hero />
      <Marquee />
      <Process />
      <WhyArco />
      <Stats />
      <Segments />
      <FAQ />
      <FinalCTA />
      <Footer />
      <MobileCtaBar />
    </main>
  );
}
